import { useCallback, useMemo } from "react";
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { QueryFunctionContext } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { parseApiError } from "utils/parseApiError";
import { HTTP_STATUS_CONFLICT } from "../../constants";
import { STATUS_POLL_INTERVAL_MS } from "./constants";
import { layoutDetectionKeys, pollLayoutDetection } from "./utils";
import type {
  LayoutDetectionStatus,
  UseLayoutDetectionArgs,
  UseLayoutDetectionResult,
} from "./types";

export const useLayoutDetection = ({
  floorPlanId,
  isEnabled,
}: UseLayoutDetectionArgs): UseLayoutDetectionResult => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reportFailure = useCallback(() => {
    toast.error(t("smartSurveyor.layoutDetection.failed"));
  }, [t]);

  const pollStatus = useMemo(
    () =>
      floorPlanId === undefined
        ? skipToken
        : ({ client }: QueryFunctionContext) =>
            pollLayoutDetection({
              client,
              floorPlanId,
              onRunFailed: reportFailure,
            }),
    [floorPlanId, reportFailure],
  );

  const statusQuery = useQuery({
    queryKey: layoutDetectionKeys.detail(floorPlanId),
    queryFn: pollStatus,
    enabled: isEnabled && floorPlanId !== undefined,
    refetchInterval: (query) =>
      query.state.data?.state === "running" ? STATUS_POLL_INTERVAL_MS : false,
  });

  const detectMutation = useMutation({
    mutationFn: (targetFloorPlanId: number): Promise<LayoutDetectionStatus> =>
      api.detectQuantitySurveyorFloorPlanLayout(targetFloorPlanId),
    onSuccess: (status, targetFloorPlanId) => {
      queryClient.setQueryData(
        layoutDetectionKeys.detail(targetFloorPlanId),
        status,
      );
    },
    onError: (error, targetFloorPlanId) => {
      // Already detected (someone else was first): the fresh status hides the button.
      if (parseApiError({ error }).status === HTTP_STATUS_CONFLICT) {
        queryClient.invalidateQueries({
          queryKey: layoutDetectionKeys.detail(targetFloorPlanId),
        });
        return;
      }
      reportFailure();
    },
  });

  const { mutateAsync: detectRequest } = detectMutation;
  const detect = useCallback(async () => {
    if (floorPlanId === undefined) return;
    try {
      await detectRequest(floorPlanId);
    } catch {
      // Handled by the mutation: a toast, or a fresh status.
    }
  }, [floorPlanId, detectRequest]);

  const status = statusQuery.data;

  return {
    isAvailable: status?.available === true,
    // From the click itself, so the button does not flicker before the agent answers.
    isRunning: detectMutation.isPending || status?.state === "running",
    isDone: status?.state === "done",
    detect,
  };
};
